import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

interface RuntimeAuthConfig {
  tenantId: string;
  clientId: string;
  apiScope: string;
}

let msal: PublicClientApplication | null = null;
let authConfig: RuntimeAuthConfig | null = null;

// Auth config is fetched at runtime from the host (`GET /admin/config.json`),
// which serves it from its own runtime configuration (env vars) — it is NOT
// baked into the bundle at build time. This keeps the image environment-
// agnostic: one image is pointed at a tenant/app registration purely via
// deployment env vars. The Azure AD SPA app registration must list the
// deployed origin's `/admin/` (and local dev origins) as redirect URIs.
async function loadAuthConfig(): Promise<RuntimeAuthConfig> {
  const res = await fetch(`${window.location.origin}/admin/config.json`, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to load /admin/config.json (HTTP ${res.status})`);
  }
  const cfg = (await res.json()) as Partial<RuntimeAuthConfig>;
  if (!cfg.tenantId || !cfg.clientId || !cfg.apiScope) {
    throw new Error('Incomplete auth config from /admin/config.json — tenantId, clientId and apiScope are all required');
  }
  return cfg as RuntimeAuthConfig;
}

export async function initialiseMsal(): Promise<void> {
  authConfig = await loadAuthConfig();

  const config: Configuration = {
    auth: {
      clientId: authConfig.clientId,
      authority: `https://login.microsoftonline.com/${authConfig.tenantId}`,
      redirectUri: `${window.location.origin}/admin/`,
      // Sign-out lands the browser back on the SPA root, which is the public landing page
      // (LandingView). That way the operator sees the marketing surface and an explicit
      // Sign-in CTA again rather than being dropped on a blank Microsoft logout page.
      postLogoutRedirectUri: `${window.location.origin}/admin/`,
      navigateToLoginRequestUrl: true,
    },
    cache: { cacheLocation: 'sessionStorage' },
    system: {
      allowNativeBroker: false, // Prevents some issues in certain environments
    }
  };
  msal = new PublicClientApplication(config);
  await msal.initialize();

  // Resolve any pending redirect (login or acquireTokenRedirect) FIRST. This is also
  // what clears MSAL's `interaction.status` sessionStorage flag — if a prior
  // interaction was interrupted (tab closed mid-redirect, navigated away), failing
  // to await this leaves the flag set and every later interactive call throws
  // `interaction_in_progress`.
  const redirectResult = await msal.handleRedirectPromise();

  // Pin an active account so getActiveAccount() in the token interceptor is stable
  // across reloads and redirect round-trips (getAllAccounts()[0] is order-fragile
  // once more than one account has ever signed in on this origin).
  const account = redirectResult?.account ?? msal.getAllAccounts()[0] ?? null;
  if (account) {
    msal.setActiveAccount(account);
  }
}

export function getMsal(): PublicClientApplication {
  if (!msal) throw new Error('MSAL not initialised — call initialiseMsal() first');
  return msal;
}

export const apiScope = (): string => {
  if (!authConfig) throw new Error('Auth config not loaded — call initialiseMsal() first');
  return authConfig.apiScope;
};
